import React, { useRef, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Calendar,
  Checkbox,
  ColumnDatas,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  InputArea,
  Modal,
  Table,
  Tooltip,
  Typography,
  useToast,
  Avatargroup,
} from "fds";
import "./index.scss";
import { DocInfo, RequestRespond } from "../../types/files";
import utilsDocs from "../../shared/utils/docs";
import { Member } from "../../types/workgroup";
import useCheckboxGroup from "../../shared/hooks/useCheckboxGroup";

import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import utilsCommon from "../../shared/utils/common";
import dayjs from "dayjs";
import { nanoid } from "@reduxjs/toolkit";
import { Trans } from "react-i18next";
import { t } from "i18next";

interface ReadRequestModalProps {
  open: boolean;
  docInfo?: DocInfo;
  onClose: () => void;
  onBack?: () => void;
}

const ReadRequestModal: React.FC<ReadRequestModalProps> = ({
  open,
  onClose,
  onBack,
  docInfo,
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";
  //전송 시점 버튼
  const SEND_TYPE = [
    { label: t("file.viewRequest.sendNow"), key: "now" },
    { label: t("file.viewRequest.sendSchedule"), key: "schedule" },
  ];
  //재전송 시점 버튼
  const RE_SEND_TYPE = Array(7)
    .fill(0)
    .map((item, index) => {
      return {
        label:
          index === 0
            ? t("file.viewRequest.noSend")
            : t("file.viewRequest.dayAfter", { day: index }),
        key: index,
      };
    });
  /** Hook 정리 */
  const toasts = useToast();
  const checkboxGroup = useCheckboxGroup<string>();
  const {
    documentState,
    getContentDetail,
    doRequestRead,
    getReadRequestList,
    deleteReadRequest,
  } = useDocumentStore();
  const documentListNew = useRef([] as ColumnDatas[]);

  /** State 정리 */
  const [memeberList, changeMemberList] = useState<Member[]>([]); // 요청 전송할 수 있는 문서 권한이 있는 사용자
  const [type, changeType] = useState<
    "READ_REQUEST" | "READ_REQUEST_SETTING" | "READ_REQUEST_HISTORY"
  >("READ_REQUEST"); // 모달 타입 READ_REQUEST: 열람 요청 , READ_REQUEST_SETTING: 열람 요청 설정, READ_REQUEST_HISTORY: 예약된 열람 요청 이력
  const [key, setKey] = useState(0); // key
  const [readRequestSetting, changeReadRequestSetting] = useState({
    sendPoint: "now",
    requestTime: 0,
    resendPoint: 0, //미열람시 재전송. 0일 경우 전송 안함
  }); // 열람 요청 설정 내용
  const [readRequestFilter, changeReadRequestFilter] = useState({
    syncId: docInfo?.syncId,
    targetUserList: [],
    requestInterval: 0,
    requestTime: 0,
    comment: "",
    timezoneOffset: new Date().getTimezoneOffset(),
  }); // 열람 요청 전송 내용

  /** useEffect 정리 */
  useEffect(() => {
    if (!docInfo) return;
    if (docInfo?.workgroup.workgroupId !== "NONEWORKGROUP") {
      changeMemberList(
        docInfo?.workgroup?.members?.filter(
          (item) => item.status === 1 && item.userId !== myId
        ) ?? []
      );
      checkboxGroup.handleAllClick([]);
    } else {
      getContentDetail({
        syncId: docInfo?.syncId,
        workgroupId: "NONEWORKGROUP",
      });
    }
    changeReadRequestFilter({
      syncId: docInfo?.syncId,
      targetUserList: [],
      requestInterval: 0,
      requestTime: 0,
      comment: "",
      timezoneOffset: new Date().getTimezoneOffset(),
    });
    getReadRequestList(docInfo?.syncId);
  }, [docInfo]);
  useEffect(() => {
    if (docInfo?.workgroup.workgroupId === "NONEWORKGROUP") {
      changeMemberList(
        documentState.contentDetail?.permissionPolicyList
          ?.filter(
            (item: any) => item.targetValue !== myId && item.status === 1
          )
          ?.map((item: any) => {
            return {
              userName: item?.userName,
              userId: item.targetValue,
              userType: item.userType,
              deptName: item.deptName,
              companyName: item.companyName,
            };
          })
      );
      checkboxGroup.handleAllClick(
        documentState.contentDetail?.permissionPolicyList
          ?.filter(
            (item: any) => item.targetValue !== myId && item.status === 1
          )
          ?.map((item: any) => item.targetValue)
      );
    }
  }, [documentState.contentDetail]);
  useEffect(() => {
    let newDataContent = [] as ColumnDatas[];
    documentState.requestReadList?.forEach(
      (item: RequestRespond, index: number) => {
        newDataContent.push(getTableData(item, index));
      }
    );
    documentListNew.current = newDataContent;
    setKey(key + 1);
  }, [documentState.requestReadList]);

  /** 함수 정리 */
  /** UI: 예약된 열람 요청 테이블 */
  const getTableData = (row: RequestRespond, index: any) => {
    return {
      data: [
        {
          columnText: <Box width={"fit-content"}></Box>,
        },
        {
          columnText: (
            <Box width={"fit-content"} style={{ padding: "14px 16px 14px 0" }}>
              <Tooltip
                message={row?.targetUserList
                  ?.map((item) => item.targetUserName || item.targetUser)
                  .join(", ")}
              >
                <Avatargroup
                  size={"24"}
                  items={[...(row?.targetUserList ?? [])]?.map((item) => {
                    return {
                      uniqueCode: utilsCommon.getUserColor(item.targetUserType),
                      name: item.targetUserName,
                      icon: item.targetUserName ? undefined : "user",
                    };
                  })}
                />
              </Tooltip>
            </Box>
          ),
        },
        {
          columnText: (
            <Box width={"fit-content"}>
              {row.comment && (
                <Tooltip message={row.comment}>
                  <Box classes="table-hover">
                    <Icons variant="line" label="memo" />
                  </Box>
                </Tooltip>
              )}
            </Box>
          ),
        },
        {
          columnText: (
            <Box width={"fit-content"}>
              {row.requestSendTime
                ? utilsCommon.renderDateFormat(
                    row.requestSendTime,
                    "YYYY-MM-DD"
                  )
                : utilsCommon.renderDateFormat(row.requestTime, "YYYY-MM-DD") +
                  `  (${t("common.schedule")})`}
            </Box>
          ),
        },
        {
          columnText: <Box width={"fit-content"}></Box>,
        },
        {
          columnText: (
            <Box width={"fit-content"}>
              {row.requestInterval > 0 && (
                <Tooltip
                  message={t("file.viewRequest.dayAfter", {
                    day: row.requestInterval,
                  })}
                >
                  <Box classes="table-hover">
                    <Icons
                      variant="line"
                      label="check"
                      stroke={getColor("COLOR_ICON_BRAND")}
                    />
                  </Box>
                </Tooltip>
              )}
            </Box>
          ),
        },
      ],
      hover: (
        <Box style={{ marginRight: "8px" }}>
          <Tooltip message={t("common.delete")} place="top">
            <Button
              btnStyle="icon"
              onClick={() => handleDeleteRequest(row.requestId)}
              leadingIcon={<Icons variant="line" label="delete" />}
            />
          </Tooltip>
        </Box>
      ),
      id: row.syncId,
    } as ColumnDatas;
  };
  /** 열람 요청 설정 닫기 */
  const closeReadRequestSettingModal = () => {
    changeType("READ_REQUEST");
    changeReadRequestSetting({
      sendPoint: readRequestFilter.requestTime === 0 ? "now" : "schedule",
      requestTime: readRequestFilter.requestTime,
      resendPoint: readRequestFilter.requestInterval,
    });
  };
  /** 열람 요청 설정 */
  const handleSubmitReadRequestSetting = () => {
    changeType("READ_REQUEST");
    changeReadRequestFilter({
      ...readRequestFilter,
      requestInterval: readRequestSetting.resendPoint,
      requestTime: readRequestSetting.requestTime,
    });
  };
  /** UI: 열람 요청 설정 화면 */
  const renderReadRequestSetting = () => {
    return (
      <Modal
        open={true}
        onClose={onClose}
        classes="readRequest-setting"
        width={540}
        isBack
        bodyPadding="0 28px"
        onBack={closeReadRequestSettingModal}
        title={t("file.viewRequest.setting")}
        isFooterDivider
        footer={
          <Box>
            <Button
              label={t("common.cancel")}
              btnStyle="fill"
              type="secondary"
              onClick={closeReadRequestSettingModal}
            />
            <Button
              label={t("common.apply")}
              btnStyle="fill"
              onClick={handleSubmitReadRequestSetting}
            />
          </Box>
        }
        body={
          <Box direction="column" height={384}>
            <Typography type="subtitle2">
              {t("file.viewRequest.sendDate")}
            </Typography>
            <Typography
              type="body4_rg"
              classes="mg mt-4"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("file.viewRequest.setSendDate")}
            </Typography>
            <Dropdown
              btnStyle="line"
              placeholder="asdf"
              value={
                SEND_TYPE?.filter(
                  (item) => item.key === readRequestSetting.sendPoint
                )[0].label
              }
              classes="mg mt-6"
              dropdownMaxWidth={224}
              width={224}
              buttonClick={key}
            >
              {SEND_TYPE.map((item) => (
                <DropdownItem
                  checkColor={readRequestSetting.sendPoint === item.key}
                  checked={readRequestSetting.sendPoint === item.key}
                  label={item.label}
                  onClick={() => {
                    setKey(key + 1);
                    changeReadRequestSetting({
                      ...readRequestSetting,
                      requestTime:
                        item.key === "now"
                          ? 0
                          : dayjs().add(1, "day").valueOf(),
                      sendPoint: item.key,
                    });
                  }}
                />
              ))}
            </Dropdown>
            {readRequestSetting?.sendPoint !== "now" && (
              <Box classes="mg mt-12 readRequest-date">
                <Typography type="body2_rg">
                  {t("file.scheduledDate")}
                </Typography>
                <Calendar
                  type="single"
                  width={228}
                  classes="mg ml-12"
                  placeholder={dayjs().format("YYYY-MM-DD")}
                  minDate={new Date(dayjs().add(1, "day").valueOf())}
                  selected={new Date(readRequestSetting.requestTime)}
                  onSubmit={(date) =>
                    changeReadRequestSetting({
                      ...readRequestSetting,
                      requestTime: dayjs(
                        date instanceof Date ? date : ""
                      ).valueOf(),
                    })
                  }
                />
              </Box>
            )}
            <Typography type="subtitle2" classes="mg mt-32">
              {t("file.viewRequest.reReqeustIf")}
            </Typography>
            <Typography
              type="body4_rg"
              classes="mg mt-4"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("file.viewRequest.reRequestIfDesc")}
            </Typography>
            <Dropdown
              btnStyle="line"
              placeholder="asdf"
              value={
                RE_SEND_TYPE.filter(
                  (item) => item.key === readRequestSetting.resendPoint
                )[0].label
              }
              classes="mg mt-6"
              dropdownMaxWidth={224}
              positionStrategy="fixed"
              width={224}
              buttonClick={key}
            >
              {RE_SEND_TYPE.map((item) => (
                <DropdownItem
                  style={{ position: "fixed", marginTop: "8px" }}
                  checkColor={readRequestSetting.resendPoint === item.key}
                  checked={readRequestSetting.resendPoint === item.key}
                  label={item.label}
                  onClick={() => {
                    setKey(key + 1);
                    changeReadRequestSetting({
                      ...readRequestSetting,
                      resendPoint: item.key,
                    });
                  }}
                />
              ))}
            </Dropdown>
          </Box>
        }
      />
    );
  };
  /** UI: 열람 요청 이력 화면 */
  const renderReadReuestHistory = () => {
    return (
      <Modal
        open={true}
        onClose={onClose}
        width={600}
        isBack
        onBack={() => changeType("READ_REQUEST")}
        title={t("file.viewRequest.scheduledList")}
        isFooter={false}
        classes="readRequest-history"
        body={
          documentListNew.current?.length === 0 ? (
            <Box direction="column" height={652} alignItems="center">
              <img
                src={require("../../shared/images/pages/File/noPeople.png")}
                alt={"thumbnail"}
                width={200}
                height={120}
                style={{ marginTop: "120px" }}
              />
              <Typography
                type="body4_rg"
                whiteSpace="pre"
                textAlign="center"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("file.viewRequest.noScheduled")}
              </Typography>
              <Button
                btnStyle="fill"
                size="sm"
                label={t("file.action.sendReadRequest")}
                width="fit-content"
                classes="mg mt-20"
                onClick={() => changeType("READ_REQUEST")}
              />
            </Box>
          ) : (
            <Box direction="column">
              <Table
                width={"100%"}
                columnHeight="md"
                data={documentListNew.current}
                totalCount={documentListNew.current?.length}
                height={"612px"}
                endReached={() => console.log(" ")}
                header={[
                  {
                    headerText: "",
                    width: "16px",
                  },
                  {
                    headerText: t("file.viewRequest.requestPeople"),
                    width: "calc(100% - 344px)",
                  },
                  {
                    headerText: "",
                    width: "48px",
                  },
                  {
                    headerText: t("file.viewRequest.sendDate"),
                    width: "160px",
                  },
                  {
                    headerText: "",
                    width: "16px",
                  },
                  {
                    headerText: t("file.viewRequest.reReqeustIf"),
                    width: "104px",
                  },
                ]}
              />
            </Box>
          )
        }
      />
    );
  };
  /** 열람 요청 삭제 */
  const handleDeleteRequest = async (requestId: string) => {
    if (!docInfo?.syncId) return;
    let resp: any = await deleteReadRequest(docInfo.syncId, requestId);
    if (resp.payload) {
      toasts.toastMsg(
        nanoid(),
        t("file.viewRequest.alert.deleteReqeuestSuccess"),
        "success"
      );
    } else if (resp.error) {
      toasts.toastMsg(
        nanoid(),
        resp.error.message ?? t("common.fail"),
        "error"
      );
    }
  };
  /** 열람 요청 생성 */
  const handleSentReadRequest = async () => {
    if (!docInfo?.syncId) return;
    let resp: any = await doRequestRead({
      ...readRequestFilter,
      syncId: docInfo.syncId,
      targetUserList:
        checkboxGroup.selected
          ?.filter((item1) => item1 !== myId)
          .map((item) => item) ?? [],
      timezoneOffset: new Date().getTimezoneOffset(),
    });
    if (resp.payload) {
      toasts.toastMsg(
        nanoid(),
        t("file.viewRequest.alert.sendSuccess"),
        "success"
      );
    } else {
      toasts.toastMsg(nanoid(), t("file.viewRequest.alert.sendFail"), "error");
    }
    onBack ? onBack() : onClose();
  };
  /**   열람 요청 화면 */
  const renderReadReuest = () => {
    return (
      <Modal
        open={open}
        isCustom
        onClose={onClose}
        height={603}
        classes="readRequest"
        width={540}
      >
        <Box classes="readRequest-header" alignItems="center">
          <Box direction="column" width="calc(100% - 35px)">
            <Box alignItems="center">
              {onBack && (
                <Box classes="cursor pointer mg mr-8" onClick={onBack}>
                  <Icons
                    variant="line"
                    label="arrow"
                    classes="cursor pointer"
                  />
                </Box>
              )}
              <Typography type="h2">
                {t("file.action.sendReadRequest")}
              </Typography>
              <Tooltip message={t("file.viewRequest.scheduledList")}>
                <Button
                  btnStyle="icon"
                  state={
                    memeberList?.filter((item1) => item1.userId !== myId)
                      .length === 0
                      ? "disable"
                      : "default"
                  }
                  leadingIcon={<Icons variant="line" label="time" />}
                  onClick={() => changeType("READ_REQUEST_HISTORY")}
                  classes="mg ml-4"
                />
              </Tooltip>
            </Box>
            <Box classes="mg mt-4" alignItems="center">
              {utilsDocs.renderDocIcon(docInfo?.contentName, 24)}
              <Tooltip
                message={docInfo?.contentName}
                maxWidth={"calc(100% - 32px)"}
              >
                <Typography type="body2_rg" classes="mg ml-8" ellipsis>
                  {docInfo?.contentName}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
          <div className="mg ml-auto cursor pointer" onClick={onClose}>
            <Icons variant="line" label="closeLarge" />
          </div>
        </Box>
        {memeberList?.length > 0 ? (
          <Box direction="column">
            <Box alignItems="center">
              <Typography type="subtitle2">
                <Trans
                  i18nKey={
                    checkboxGroup.selected?.length > 0
                      ? "common.chosenMember"
                      : docInfo?.workgroup.workgroupId === "NONEWORKGROUP"
                      ? "sendingFile.recipientsWithCount"
                      : "common.memberWithCount"
                  }
                  values={{
                    count:
                      checkboxGroup.selected?.length > 0
                        ? checkboxGroup.selected?.length
                        : memeberList?.length ?? 0,
                  }}
                  components={{
                    1: (
                      <Typography
                        type="subtitle2"
                        exactColor={getColor("COLOR_TEXT_BRAND")}
                      />
                    ),
                  }}
                />
              </Typography>
              <Button
                label={
                  checkboxGroup.selected?.length !== memeberList?.length
                    ? t("common.selectAll")
                    : t("common.unselectAll")
                }
                onClick={() =>
                  checkboxGroup.handleAllClick(
                    checkboxGroup.selected?.length !== memeberList?.length
                      ? memeberList.map((item) => item.userId)
                      : []
                  )
                }
                btnStyle="link"
                classes="mg ml-auto"
                textColor={getColor("COLOR_TEXT_BRAND")}
              />
            </Box>
            <Box
              direction={"column"}
              classes="readRequest-members"
              height={checkboxGroup.selected?.length > 0 ? 278 : 388}
            >
              {memeberList
                ?.filter((item1) => item1.userId !== myId)
                ?.map((item) => (
                  <Box minHeight={50} alignItems="center" width="100%">
                    <Avatar
                      name={item?.userName ? item?.userName : item.userId}
                      uniqueCode={utilsCommon.getUserColor(
                        item.userType,
                        item.status,
                        item?.userName
                      )}
                      icon={item?.userName ? undefined : "user"}
                      size={"32"}
                    />
                    <Box
                      direction="column"
                      classes="mg ml-8"
                      width={"calc(100% - 80px)"}
                    >
                      <Tooltip
                        message={utilsCommon.getUserNameIdTag(
                          item.userId,
                          item?.userName
                        )}
                      >
                        <Typography type="body2_rg" ellipsis>
                          {utilsCommon.getUserNameIdTag(
                            item.userId,
                            item?.userName
                          )}
                        </Typography>
                      </Tooltip>

                      <Typography
                        type="caption1_rg"
                        exactColor={"#717985"}
                        classes="mg mt-2"
                        ellipsis
                      >
                        {utilsCommon.getUserPositionNDept({
                          companyName: item.companyName,
                          deptName: item.deptName,
                          userType: item.userType,
                        })}
                      </Typography>
                    </Box>
                    <Checkbox
                      check={checkboxGroup.isSelected(item.userId)}
                      onClick={() => checkboxGroup.handleClick(item.userId)}
                      roundshape
                      classDiv="mg ml-auto"
                    />
                  </Box>
                ))}
            </Box>
          </Box>
        ) : (
          <Box
            direction="column"
            alignItems="center"
            justifyContent="center"
            maxHeight={420}
            height={"100%"}
          >
            <img
              src={require("../../shared/images/pages/File/noPeople.png")}
              width={200}
              height={120}
              alt="img"
            />
            <Typography
              type="body4_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("file.viewRequest.noUsers")}
            </Typography>
          </Box>
        )}
        <Box classes="readRequest-footer " direction="column">
          <Box classes="readRequest-footer--divider" />
          {checkboxGroup.selected?.length > 0 && (
            <Box classes="readRequest-msg">
              <Typography type="subtitle2">{t("common.message")}</Typography>
              <InputArea
                placeholder={t("common.messagePlaceholder")}
                classes="mg mt-6"
                rows={3}
                maxLength={500}
                value={readRequestFilter.comment}
                onChange={(e) => {
                  changeReadRequestFilter({
                    ...readRequestFilter,
                    comment: e.target.value,
                  });
                }}
                style={{ marginBottom: "4px" }}
                size="sm"
              />
            </Box>
          )}
          <Box style={{ marginBottom: "24px" }} alignItems="flex-end">
            <Button
              label={t("file.viewRequest.setting")}
              leadingIcon={<Icons variant="line" label="setting" />}
              size="sm"
              btnStyle="link"
              onClick={() => changeType("READ_REQUEST_SETTING")}
              style={{ marginBottom: "4px" }}
            />
            <Box classes="mg ml-auto" style={{ marginTop: "12px" }}>
              <Button
                label={t("common.cancel")}
                btnStyle="fill"
                type="secondary"
                onClick={onBack ?? onClose}
              />
              <Button
                label={t("common.send")}
                classes="mg ml-8"
                btnStyle="fill"
                onClick={handleSentReadRequest}
                state={
                  checkboxGroup.selected?.length === 0 ? "disable" : "default"
                }
              />
            </Box>
          </Box>
        </Box>
      </Modal>
    );
  };
  return type === "READ_REQUEST_SETTING"
    ? renderReadRequestSetting()
    : type === "READ_REQUEST"
    ? renderReadReuest()
    : renderReadReuestHistory();
};
export default ReadRequestModal;
